import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Tooltip } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import CheckIcon from '@mui/icons-material/Check'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/Close'
import AttachmentIcon from '../icons/AttachmentIcon'
import { AiAgentMessageList } from '../../features/AiAgentChat/AiAgentMessageList'
import { QueuedMessageList } from '../../features/AiAgentChat/QueuedMessageList'
import { useAiAgentConversation } from '../../features/AiAgentChat/hooks/useAiAgentConversation'
import { usePageContext } from '../../features/AiAgentChat/hooks/usePageContext'
import {
  releaseImageAttachments,
  toUploadedImageRefs,
  useAiAgentImageUpload,
} from '../../features/AiAgentChat/hooks/useAiAgentImageUpload'
import { useAiAgentMessageQueue } from '../../features/AiAgentChat/hooks/useAiAgentMessageQueue'
import { useImageDropZone } from '../../features/AiAgentChat/hooks/useImageDropZone'
import {
  RESIZE_DIRECTIONS,
  usePanelGeometry,
} from '../../features/AiAgentChat/hooks/usePanelGeometry'
import {
  AiAgentChatDraft,
  mergeDrafts,
} from '../../features/AiAgentChat/queuedMessages'
import { AllState } from '../../../store'
import {
  clearWbsItemAttachments,
  closeChatPanel,
  removeWbsItemAttachment,
  selectIsPanelOpen,
  selectWbsItemAttachments,
  setWbsItemAttachments,
} from '../../../store/aiAgentChat'
import {
  ChatPanel,
  PanelHeader,
  HeaderTitle,
  HeaderActions,
  InputArea,
  ChatInput,
  SendButton,
  HeaderIconButton,
  InlineError,
  AttachmentsPreviewRow,
  AttachmentPreviewItem,
  AttachmentThumb,
  AttachmentUploadingOverlay,
  RemoveAttachmentButton,
  DropOverlay,
  ResizeHandle,
} from './styles'
import { WbsItemAttachmentChip } from '../../features/AiAgentChat/WbsItemAttachmentChip'

export const AiAgentChat = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [inputValue, setInputValue] = useState('')
  // 送信待ちを破棄したことの通知。会話自体のエラーとは別の事情なので独立して持つ。
  const [queueNotice, setQueueNotice] = useState<string | undefined>(undefined)
  // 開閉はヘッダーの起動ボタンからも切り替わるためReduxで持つ。
  const isPanelOpen = useSelector((state: AllState) => selectIsPanelOpen(state))
  // 画面側からpushされたWBSアイテム添付（右クリックメニュー・単票ヘッダー等から追加される）。
  const wbsItemAttachments = useSelector((state: AllState) =>
    selectWbsItemAttachments(state)
  )
  const {
    messages,
    isStreaming,
    inlineError,
    lastTurnResult,
    sendMessage,
    resetConversation,
  } = useAiAgentConversation({
    greetingText: intl.formatMessage({ id: 'aiAgentChat.greeting' }),
    busyErrorText: intl.formatMessage({ id: 'aiAgentChat.error.busy' }),
    generalErrorText: intl.formatMessage({ id: 'aiAgentChat.error.general' }),
    streamErrorText: intl.formatMessage({
      id: 'aiAgentChat.error.streaming',
    }),
  })
  const pageContext = usePageContext()
  const {
    attachments,
    errorMessage: attachErrorMessage,
    isUploading,
    addFiles,
    removeAttachment,
    clearAttachments,
    detachAttachments,
    restoreAttachments,
  } = useAiAgentImageUpload()
  const {
    queuedMessages,
    editingMessageId,
    enqueue,
    removeQueuedMessage,
    startEditing,
    commitEditing,
    cancelEditing,
    takeQueuedMessagesForSend,
    discardQueue,
  } = useAiAgentMessageQueue()
  const { isDraggingOver, dropZoneProps } = useImageDropZone({
    onDropFiles: addFiles,
  })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const chatInputRef = useRef<HTMLTextAreaElement>(null)
  const { panelRef, panelStyle, handleMoveStart, handleResizeStart } =
    usePanelGeometry()
  // 送信待ちの自動送信はターン終了イベントから実行するため、そこで参照する画面状態を
  // 最新に保つ。自動送信の effect より前に宣言して、同じ更新では先に反映させる。
  const pageContextRef = useRef(pageContext)
  useEffect(() => {
    pageContextRef.current = pageContext
  })

  const buildCurrentDraft = (): AiAgentChatDraft => ({
    text: inputValue,
    images: attachments,
    wbsItems: wbsItemAttachments,
  })

  // 送信待ちから戻ってきた内容を入力欄へ載せる。
  const applyDraft = (draft: AiAgentChatDraft) => {
    setInputValue(draft.text)
    restoreAttachments(draft.images)
    dispatch(setWbsItemAttachments(draft.wbsItems))
    chatInputRef.current?.focus()
  }

  const sendDraft = async (draft: AiAgentChatDraft) => {
    const imageRefs = toUploadedImageRefs(draft.images)
    const result = await sendMessage(
      draft.text,
      pageContextRef.current,
      imageRefs.length > 0 ? imageRefs : undefined,
      draft.wbsItems.length > 0 ? draft.wbsItems : undefined
    )
    if (result === 'busy') {
      // 応答中で受け付けられなかったため、送信待ちへ戻して次のターン終了時に送る。
      enqueue(draft)
      return
    }
    // 送信済みバブルはアップロード済みURLを表示するため、プレビューは解放してよい。
    releaseImageAttachments(draft.images)
  }

  // 応答が終わったあとに編集を確定・取消した場合、送信の契機がここにしかないため送り出す。
  const flushQueuedMessages = () => {
    if (isStreaming) {
      return
    }
    const queued = takeQueuedMessagesForSend()
    if (queued) {
      void sendDraft(queued)
    }
  }

  // 入力欄の確定操作。AI応答中は送信せず送信待ちに積み、編集中はその内容を確定する。
  const handleSubmit = () => {
    if (isUploading) {
      return
    }
    setQueueNotice(undefined)
    if (editingMessageId) {
      applyDraft(commitEditing(buildCurrentDraft()))
      flushQueuedMessages()
      return
    }
    if (inputValue.trim() === '') {
      return
    }
    const draft: AiAgentChatDraft = {
      text: inputValue.trim(),
      images: detachAttachments(),
      wbsItems: wbsItemAttachments,
    }
    setInputValue('')
    dispatch(clearWbsItemAttachments())
    if (isStreaming) {
      enqueue(draft)
    } else {
      // 応答完了後に編集を確定した場合など、送信待ちが残っていればまとめて送る。
      const queued = takeQueuedMessagesForSend()
      void sendDraft(queued ? mergeDrafts([queued, draft]) : draft)
    }
    // 送信ボタンから確定した場合もそのまま入力を続けられるようにする。
    chatInputRef.current?.focus()
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFiles(Array.from(event.target.files))
    }
    event.target.value = ''
  }

  // スクリーンショット等をクリップボードから直接添付する。
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from(event.clipboardData.files)
    if (files.length === 0) return
    // 既定の動作のままだとファイル名が入力欄に文字列として貼り付けられる。
    event.preventDefault()
    addFiles(files)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && editingMessageId) {
      event.preventDefault()
      applyDraft(cancelEditing(buildCurrentDraft()))
      flushQueuedMessages()
      return
    }
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleEditQueuedMessage = (id: string) => {
    // アップロード中の添付を入力欄から退避すると完了通知の宛先が失われ、以後送信できなくなる。
    if (isUploading) {
      return
    }
    const restored = startEditing(id, buildCurrentDraft())
    if (restored) {
      applyDraft(restored)
    }
  }

  const handleRefresh = () => {
    resetConversation()
    setInputValue('')
    clearAttachments()
    dispatch(clearWbsItemAttachments())
    discardQueue()
    setQueueNotice(undefined)
  }

  // ターン終了イベントを1度だけ拾うための連番。
  const handledTurnSeqRef = useRef(0)
  useEffect(() => {
    if (!lastTurnResult || lastTurnResult.seq <= handledTurnSeqRef.current) {
      return
    }
    handledTurnSeqRef.current = lastTurnResult.seq
    if (lastTurnResult.status === 'error') {
      // 失敗したターンの後続を自動送信しても同じ失敗を繰り返すため、送信待ちは破棄する。
      if (discardQueue() > 0) {
        setQueueNotice(
          intl.formatMessage({ id: 'aiAgentChat.queue.discarded' })
        )
      }
      return
    }
    const merged = takeQueuedMessagesForSend()
    if (merged) {
      void sendDraft(merged)
    }
    // 他の依存で再実行すると、送信待ちを取り出した直後の状態でもう一度動いてしまうため、
    // ターンの終了イベントにのみ反応させる。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastTurnResult])

  if (!isPanelOpen) return null

  const submitTooltipId = editingMessageId
    ? 'aiAgentChat.queue.commitEdit'
    : isStreaming
      ? 'aiAgentChat.queue.add'
      : 'aiAgentChat.send'
  const inputPlaceholder = intl.formatMessage({
    id: editingMessageId
      ? 'aiAgentChat.queue.editPlaceholder'
      : 'aiAgentChat.input.placeholder',
  })

  const renderSubmitIcon = () => {
    if (editingMessageId) return <CheckIcon />
    if (isStreaming) return <PlaylistAddIcon />
    return <SendIcon />
  }

  return (
    <ChatPanel
      ref={panelRef}
      style={panelStyle}
      {...dropZoneProps}
      onPaste={handlePaste}
    >
      {RESIZE_DIRECTIONS.map(direction => (
        <ResizeHandle
          key={direction}
          direction={direction}
          onMouseDown={handleResizeStart(direction)}
        />
      ))}
      {isDraggingOver && (
        <DropOverlay>
          {intl.formatMessage({ id: 'aiAgentChat.attach.dropHere' })}
        </DropOverlay>
      )}
      <PanelHeader onMouseDown={handleMoveStart}>
        <HeaderTitle>
          {intl.formatMessage({ id: 'aiAgentChat.title' })}
        </HeaderTitle>
        <HeaderActions>
          <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.reset' })}>
            <span>
              <HeaderIconButton size="small" onClick={handleRefresh}>
                <RefreshIcon fontSize="small" />
              </HeaderIconButton>
            </span>
          </Tooltip>
          <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.close' })}>
            <HeaderIconButton
              size="small"
              onClick={() => dispatch(closeChatPanel())}
            >
              <CloseIcon fontSize="small" />
            </HeaderIconButton>
          </Tooltip>
        </HeaderActions>
      </PanelHeader>
      <AiAgentMessageList messages={messages} isStreaming={isStreaming} />
      {inlineError && <InlineError>{inlineError}</InlineError>}
      {attachErrorMessage && <InlineError>{attachErrorMessage}</InlineError>}
      {queueNotice && <InlineError>{queueNotice}</InlineError>}
      <QueuedMessageList
        messages={queuedMessages}
        editingMessageId={editingMessageId}
        editingAttachmentCounts={{
          images: attachments.length,
          wbsItems: wbsItemAttachments.length,
        }}
        disableEdit={isUploading}
        onEdit={handleEditQueuedMessage}
        onRemove={removeQueuedMessage}
      />
      {(attachments.length > 0 || wbsItemAttachments.length > 0) && (
        <AttachmentsPreviewRow>
          {attachments.map(attachment => (
            <AttachmentPreviewItem
              key={attachment.id}
              hasError={attachment.status === 'error'}
            >
              <AttachmentThumb src={attachment.previewUrl} alt="" />
              {attachment.status === 'uploading' && (
                <AttachmentUploadingOverlay />
              )}
              <RemoveAttachmentButton
                size="small"
                onClick={() => removeAttachment(attachment.id)}
                aria-label={intl.formatMessage({
                  id: 'aiAgentChat.attach.remove',
                })}
              >
                <CloseIcon fontSize="inherit" />
              </RemoveAttachmentButton>
            </AttachmentPreviewItem>
          ))}
          {wbsItemAttachments.map(wbsItem => (
            <WbsItemAttachmentChip
              key={wbsItem.code}
              wbsItem={wbsItem}
              onRemove={() => dispatch(removeWbsItemAttachment(wbsItem.code))}
            />
          ))}
        </AttachmentsPreviewRow>
      )}
      <InputArea>
        <Tooltip
          title={intl.formatMessage({ id: 'aiAgentChat.attach.button' })}
        >
          <span>
            <HeaderIconButton size="small" onClick={handleAttachClick}>
              <AttachmentIcon fontSize="small" />
            </HeaderIconButton>
          </span>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={true}
          hidden={true}
          onChange={handleFileInputChange}
        />
        <ChatInput
          placeholder={inputPlaceholder}
          fullWidth={true}
          multiline={true}
          maxRows={4}
          inputProps={{ 'aria-label': inputPlaceholder }}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          inputRef={chatInputRef}
        />
        <Tooltip title={intl.formatMessage({ id: submitTooltipId })}>
          <span>
            <SendButton
              onClick={handleSubmit}
              disabled={
                isUploading || (!editingMessageId && inputValue.trim() === '')
              }
            >
              {renderSubmitIcon()}
            </SendButton>
          </span>
        </Tooltip>
      </InputArea>
    </ChatPanel>
  )
}
